import React from 'react';
import { Link } from 'react-router-dom';
import { SITE, SERVICES } from '@/data/site';
import { CITIES } from '@/data/cities';
import { DeltaMark } from '@/components/site/Header';
import { useInView } from '@/hooks/useInView';

const MORE_SERVICES = [
  ['/services/shopify-development/', 'Shopify Development'],
  ['/services/android-app-development/', 'Android App Development'],
  ['/services/flutter-app-development/', 'Flutter App Development'],
  ['/services/food-delivery-app-development/', 'Food Delivery Apps'],
  ['/services/whatsapp-chatbot-development/', 'WhatsApp Chatbots'],
  ['/services/website-maintenance/', 'Website Maintenance'],
];

const MORE_LINKS = [
  { to: '/mobile-app-development-company-texas/', label: 'App development Texas' },
  { to: '/mobile-app-development-company-florida/', label: 'App development Florida' },
  { to: '/software-development-company-california/', label: 'Software development California' },
];

const fLink = 'inline-flex text-sm text-[#C9C9D3] hover:text-white transition-[color,transform] duration-300 [transition-timing-function:var(--ease-out)] hover:translate-x-1';

function Wordmark() {
  const [ref, inView] = useInView({ threshold: 0.05 });
  const letters = 'NioDelta'.split('');
  return (
    <div
      ref={ref}
      aria-hidden
      className={`flex justify-center mt-20 md:mt-24 font-display font-semibold leading-[0.76] tracking-[-0.07em] select-none overflow-hidden pt-[0.04em] text-[clamp(80px,21.5vw,340px)] ${inView ? 'in-view' : ''}`}
    >
      {letters.map((l, i) => (
        <span key={i} className="word !overflow-visible !p-0 !m-0">
          <span
            style={{
              transitionDelay: `${i * 45}ms`,
              background: i < 3
                ? 'linear-gradient(180deg, #EEEEF2 10%, rgba(238,238,242,0.04) 95%)'
                : 'linear-gradient(180deg, #8C7CFF 0%, rgba(75,159,255,0.06) 95%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {l}
          </span>
        </span>
      ))}
    </div>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.08] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2.5" aria-label="NioDelta home">
              <DeltaMark className="h-7 w-7" />
              <span className="font-display text-[17px] font-semibold tracking-[-0.02em]">Nio<span className="text-ink-muted">Delta</span></span>
            </Link>
            <p className="mt-5 max-w-sm text-[15px] text-ink-muted leading-relaxed">
              A small studio in India, working with US clients on US hours. Crafted, not templated. We partner with a handful of teams a year—if you’re building the next chapter of a real product, we’re here for the long form.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6">
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="footer-cta-whatsapp" className="nd-link-u text-sm text-white">
                Book discovery call ↗
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="footer-cta-email" className="nd-link-u text-sm text-white">
                {SITE.email}
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h2 className="nd-mono mb-5 !text-[12px]">Services</h2>
            <ul className="grid gap-2.5">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}/`} className={fLink}>{s.title}</Link></li>
              ))}
              {MORE_SERVICES.map(([to, label]) => (
                <li key={to}><Link to={to} className={fLink}>{label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h2 className="nd-mono mb-5 !text-[12px]">Company</h2>
            <ul className="grid gap-2.5">
              <li><Link to="/work/" className={fLink}>Work</Link></li>
              <li><Link to="/about/" className={fLink}>About</Link></li>
              <li><Link to="/website-development-cost/" className={fLink}>Pricing</Link></li>
              <li><Link to="/mobile-app-development-cost/" className={fLink}>App development cost</Link></li>
              <li><Link to="/contact/" className={fLink}>Contact</Link></li>
              <li><a href="mailto:careers@niodelta.com" className={fLink}>Careers</a></li>
            </ul>
            <h2 className="nd-mono mt-10 mb-5 !text-[12px]">Connect</h2>
            <ul className="grid gap-2.5">
              {SITE.socials.map((s) => (
                <li key={s.label}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className={fLink}>{s.label} ↗</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h2 className="nd-mono mb-5 !text-[12px]">Locations</h2>
            <ul className="grid gap-2.5">
              {CITIES.map((c) => (
                <li key={c.slug}><Link to={`${c.path}/`} className={fLink}>Web development {c.name}</Link></li>
              ))}
              {MORE_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} className={fLink}>{l.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <Wordmark />

        <div className="py-6 border-t border-white/[0.08] flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[13px] text-ink-muted">
          <div>© {year} NioDelta Web Studio · {SITE.location}</div>
          <div className="flex items-center gap-6">
            <Link to="/policy/" className="nd-link-u hover:text-white">Privacy Policy</Link>
            <Link to="/tos/" className="nd-link-u hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
